import { useMemo } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import {
  ERROR_TYPES,
  FUNDAMENTAL_MODES,
  type FundamentalExerciseOutput,
  type FundamentalQuestionOutput,
  type ErrorType,
} from '@jeraisonne/ai';

/**
 * Éditeur d'exercice fondamental — série de questions QCM **indépendantes**.
 * Volontairement séparé de SolvePathEditor : pas d'équations pas-à-pas, pas de
 * sous-questions hiérarchiques. Une question = un exercise_step autonome.
 */

const MAX_QUESTIONS = 40;

const MODE_LABEL: Record<(typeof FUNDAMENTAL_MODES)[number], string> = {
  LESSON_RECALL: 'Rappel de cours',
  CALCULATION: 'Calcul direct',
  FORMULA: 'Formules',
  THEOREM: 'Théorèmes',
  MIXED: 'Mixte',
};

const ERROR_TYPE_LABEL: Record<ErrorType, string> = {
  calcul: 'Calcul',
  raisonnement: 'Raisonnement',
  lecture: 'Lecture',
  signe: 'Signe',
};

export type CompetenceOption = { slug: string; title: string };

type Props = {
  value: FundamentalExerciseOutput;
  onChange: (next: FundamentalExerciseOutput) => void;
  onPublish: (status: 'draft' | 'published') => void;
  onRegenerate?: () => void;
  pending: boolean;
  /** Compétences du chapitre pour le sélecteur par question. */
  competences?: CompetenceOption[];
  labels?: { draft: string; publish: string };
};

type Issue = { questionIndex: number; message: string };

/** Valide la cohérence pédagogique avant publication. */
export function validateFundamental(value: FundamentalExerciseOutput): Issue[] {
  const issues: Issue[] = [];
  if (value.questions.length === 0) {
    issues.push({ questionIndex: -1, message: 'Au moins une question est requise.' });
  }
  if (value.questions.length > MAX_QUESTIONS) {
    issues.push({ questionIndex: -1, message: `Maximum ${MAX_QUESTIONS} questions.` });
  }
  value.questions.forEach((q, i) => {
    if (!q.prompt_md.trim()) {
      issues.push({ questionIndex: i, message: 'Énoncé de la question vide.' });
    }
    if (!q.explanation_md.trim()) {
      issues.push({ questionIndex: i, message: 'Explication requise.' });
    }
    const correctCount = q.choices.filter((c) => c.is_correct).length;
    if (correctCount !== 1) {
      issues.push({
        questionIndex: i,
        message: `Exactement 1 bonne réponse requise (${correctCount} actuellement).`,
      });
    }
    if (q.choices.length < 3 || q.choices.length > 4) {
      issues.push({ questionIndex: i, message: '3 ou 4 choix par question.' });
    }
    if (q.choices.some((c) => !c.label_md.trim())) {
      issues.push({ questionIndex: i, message: 'Un choix a un libellé vide.' });
    }
  });
  return issues;
}

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-xl border px-3 py-2 font-sans text-sm focus:outline-none focus:ring-2';
const selectCls =
  'border-ink-200 text-ink-900 focus:ring-primary-400 h-10 appearance-none rounded-xl border bg-white px-3 font-sans text-sm focus:outline-none focus:ring-2';

export function FundamentalExerciseEditor({
  value,
  onChange,
  onPublish,
  onRegenerate,
  pending,
  competences = [],
  labels = { draft: 'Sauver en brouillon', publish: 'Publier' },
}: Props) {
  const issues = useMemo(() => validateFundamental(value), [value]);
  const isValid = issues.length === 0;

  function patch(next: Partial<FundamentalExerciseOutput>) {
    onChange({ ...value, ...next });
  }

  function patchQuestion(qi: number, next: Partial<FundamentalQuestionOutput>) {
    patch({
      questions: value.questions.map((q, i) => (i === qi ? { ...q, ...next } : q)),
    });
  }

  function moveQuestion(qi: number, dir: -1 | 1) {
    const target = qi + dir;
    if (target < 0 || target >= value.questions.length) return;
    const next = [...value.questions];
    [next[qi], next[target]] = [next[target]!, next[qi]!];
    patch({ questions: next });
  }

  function removeQuestion(qi: number) {
    patch({ questions: value.questions.filter((_, i) => i !== qi) });
  }

  function addQuestion() {
    if (value.questions.length >= MAX_QUESTIONS) return;
    patch({
      questions: [
        ...value.questions,
        {
          prompt_md: '',
          explanation_md: '',
          hint_md: null,
          difficulty: null,
          competence_slug: null,
          choices: [
            { label_md: '', is_correct: true, error_type: null, feedback_md: null },
            { label_md: '', is_correct: false, error_type: 'calcul', feedback_md: null },
            { label_md: '', is_correct: false, error_type: 'raisonnement', feedback_md: null },
          ],
        },
      ],
    });
  }

  function patchChoice(
    qi: number,
    ci: number,
    next: Partial<FundamentalQuestionOutput['choices'][number]>,
  ) {
    const q = value.questions[qi]!;
    patchQuestion(qi, {
      choices: q.choices.map((c, i) => (i === ci ? { ...c, ...next } : c)),
    });
  }

  function setCorrectChoice(qi: number, ci: number) {
    const q = value.questions[qi]!;
    patchQuestion(qi, {
      choices: q.choices.map((c, i) => ({
        ...c,
        is_correct: i === ci,
        error_type: i === ci ? null : (c.error_type ?? 'calcul'),
      })),
    });
  }

  function addChoice(qi: number) {
    const q = value.questions[qi]!;
    if (q.choices.length >= 4) return;
    patchQuestion(qi, {
      choices: [
        ...q.choices,
        { label_md: '', is_correct: false, error_type: 'calcul', feedback_md: null },
      ],
    });
  }

  function removeChoice(qi: number, ci: number) {
    const q = value.questions[qi]!;
    if (q.choices.length <= 3) return;
    patchQuestion(qi, { choices: q.choices.filter((_, i) => i !== ci) });
  }

  return (
    <div className="space-y-5">
      {/* Métadonnées */}
      <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
        <div>
          <label className="font-display text-ink-700 mb-1 block text-[12px] font-semibold">
            Titre de la banque
          </label>
          <input
            value={value.title}
            onChange={(e) => patch({ title: e.target.value })}
            placeholder="Ex : Dérivées usuelles — échauffement"
            className={inputCls}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="font-display text-ink-700 mb-1 block text-[12px] font-semibold">
              Difficulté
            </label>
            <select
              value={value.difficulty}
              onChange={(e) =>
                patch({ difficulty: e.target.value as FundamentalExerciseOutput['difficulty'] })
              }
              className={cn(selectCls, 'w-full')}
            >
              <option value="easy">Facile</option>
              <option value="medium">Moyen</option>
              <option value="hard">Difficile</option>
            </select>
          </div>
          <div>
            <label className="font-display text-ink-700 mb-1 block text-[12px] font-semibold">
              Durée (min)
            </label>
            <input
              type="number"
              min={1}
              max={120}
              value={value.estimated_minutes}
              onChange={(e) =>
                patch({ estimated_minutes: Math.max(1, Number(e.target.value) || 1) })
              }
              className={cn(inputCls, 'h-10 py-0')}
            />
          </div>
          <div>
            <label className="font-display text-ink-700 mb-1 block text-[12px] font-semibold">
              Type pédagogique
            </label>
            <select
              value={value.fundamental_mode}
              onChange={(e) =>
                patch({
                  fundamental_mode: e.target.value as FundamentalExerciseOutput['fundamental_mode'],
                })
              }
              className={cn(selectCls, 'w-full')}
            >
              {FUNDAMENTAL_MODES.map((m) => (
                <option key={m} value={m}>
                  {MODE_LABEL[m]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Validation */}
      {isValid ? (
        <div className="bg-xp-50 border-xp-100 text-xp-700 flex items-center gap-2 rounded-xl border px-4 py-2.5 font-sans text-sm">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {value.questions.length} question{value.questions.length > 1 ? 's' : ''} — prêt à publier.
        </div>
      ) : (
        <div className="bg-error-50 border-error-100 rounded-xl border px-4 py-2.5">
          <div className="text-error-700 mb-1 flex items-center gap-2 font-sans text-sm font-semibold">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {issues.length} problème{issues.length > 1 ? 's' : ''} à corriger
          </div>
          <ul className="text-error-600 list-disc space-y-0.5 pl-7 font-sans text-xs">
            {issues.slice(0, 8).map((iss, i) => (
              <li key={i}>
                {iss.questionIndex >= 0 ? `Question ${iss.questionIndex + 1} — ` : ''}
                {iss.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Questions */}
      <div className="space-y-4">
        {value.questions.map((q, qi) => (
          <div key={qi} className="border-ink-200 rounded-2xl border bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-display text-ink-900 text-sm font-extrabold">
                Question {qi + 1}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveQuestion(qi, -1)}
                  disabled={qi === 0}
                  className="text-ink-400 hover:text-ink-700 disabled:opacity-30"
                  aria-label="Monter la question"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveQuestion(qi, 1)}
                  disabled={qi === value.questions.length - 1}
                  className="text-ink-400 hover:text-ink-700 disabled:opacity-30"
                  aria-label="Descendre la question"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeQuestion(qi)}
                  className="text-error-400 hover:text-error-600 ml-1"
                  aria-label="Supprimer la question"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <textarea
                value={q.prompt_md}
                onChange={(e) => patchQuestion(qi, { prompt_md: e.target.value })}
                rows={2}
                placeholder="Énoncé de la question (Markdown + LaTeX)…"
                className={cn(inputCls, 'resize-y')}
              />

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <select
                  value={q.difficulty ?? ''}
                  onChange={(e) =>
                    patchQuestion(qi, {
                      difficulty: (e.target.value ||
                        null) as FundamentalQuestionOutput['difficulty'],
                    })
                  }
                  className={cn(selectCls, 'w-full')}
                >
                  <option value="">Difficulté héritée</option>
                  <option value="easy">Facile</option>
                  <option value="medium">Moyen</option>
                  <option value="hard">Difficile</option>
                </select>
                <select
                  value={q.competence_slug ?? ''}
                  onChange={(e) => patchQuestion(qi, { competence_slug: e.target.value || null })}
                  className={cn(selectCls, 'w-full')}
                >
                  <option value="">Aucune compétence</option>
                  {competences.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Choix */}
              <div className="space-y-2">
                {q.choices.map((c, ci) => (
                  <div
                    key={ci}
                    className={cn(
                      'rounded-xl border p-2.5',
                      c.is_correct ? 'border-xp-200 bg-xp-50' : 'border-ink-100 bg-ink-50/50',
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <label className="mt-2 flex shrink-0 items-center" title="Bonne réponse">
                        <input
                          type="radio"
                          name={`correct-${qi}`}
                          checked={c.is_correct}
                          onChange={() => setCorrectChoice(qi, ci)}
                        />
                      </label>
                      <div className="min-w-0 flex-1 space-y-2">
                        <input
                          value={c.label_md}
                          onChange={(e) => patchChoice(qi, ci, { label_md: e.target.value })}
                          placeholder={`Choix ${ci + 1}`}
                          className={inputCls}
                        />
                        {!c.is_correct && (
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              value={c.error_type ?? 'calcul'}
                              onChange={(e) =>
                                patchChoice(qi, ci, { error_type: e.target.value as ErrorType })
                              }
                              className={cn(selectCls, 'h-9 text-xs')}
                            >
                              {ERROR_TYPES.map((t) => (
                                <option key={t} value={t}>
                                  {ERROR_TYPE_LABEL[t]}
                                </option>
                              ))}
                            </select>
                            <input
                              value={c.feedback_md ?? ''}
                              onChange={(e) =>
                                patchChoice(qi, ci, { feedback_md: e.target.value || null })
                              }
                              placeholder="Feedback (optionnel)"
                              className={cn(inputCls, 'h-9 flex-1 py-0 text-xs')}
                            />
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeChoice(qi, ci)}
                        disabled={q.choices.length <= 3}
                        className="text-ink-300 hover:text-error-600 mt-2 shrink-0 disabled:opacity-30"
                        aria-label="Supprimer le choix"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                {q.choices.length < 4 && (
                  <button
                    type="button"
                    onClick={() => addChoice(qi)}
                    className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 font-sans text-xs font-semibold"
                  >
                    <Plus className="h-3.5 w-3.5" /> Ajouter un choix
                  </button>
                )}
              </div>

              <textarea
                value={q.explanation_md}
                onChange={(e) => patchQuestion(qi, { explanation_md: e.target.value })}
                rows={2}
                placeholder="Explication (affichée après la réponse)…"
                className={cn(inputCls, 'resize-y')}
              />
              <textarea
                value={q.hint_md ?? ''}
                onChange={(e) => patchQuestion(qi, { hint_md: e.target.value || null })}
                rows={1}
                placeholder="Indice (optionnel)…"
                className={cn(inputCls, 'resize-y')}
              />
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addQuestion}
        disabled={value.questions.length >= MAX_QUESTIONS}
        className="border-ink-200 text-ink-600 hover:border-primary-300 hover:text-primary-600 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 font-sans text-sm transition-colors active:scale-[0.98] disabled:opacity-40"
      >
        <Plus className="h-4 w-4" /> Ajouter une question
      </button>

      {/* Actions */}
      <div className="border-ink-100 flex flex-col gap-3 border-t pt-4 sm:flex-row">
        <Button
          onClick={() => onPublish('published')}
          disabled={!isValid || pending}
          className="w-full sm:w-auto"
        >
          {labels.publish}
        </Button>
        <Button
          variant="outline"
          onClick={() => onPublish('draft')}
          disabled={value.questions.length === 0 || pending}
          className="w-full sm:w-auto"
        >
          {labels.draft}
        </Button>
        {onRegenerate && (
          <Button
            variant="ghost"
            onClick={onRegenerate}
            disabled={pending}
            className="w-full sm:w-auto"
          >
            Régénérer
          </Button>
        )}
      </div>
    </div>
  );
}
